import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogSmartLinkCellProps {
  item: CatalogItem;
  onOpenSmartLink: (smartLinkId: number) => void;
  onCreateLink: (item: CatalogItem) => void;
}

/** The page this release has, or the button that starts one. */
const CatalogSmartLinkCellComponent: React.FC<CatalogSmartLinkCellProps> = ({
  item,
  onOpenSmartLink,
  onCreateLink,
}) => {
  const intl = useIntl();
  const { smartLinkId, smartLinkSlug, smartLinkStatus } = item;

  const open = useCallback(() => {
    if (smartLinkId === null) {
      return;
    }
    onOpenSmartLink(smartLinkId);
  }, [onOpenSmartLink, smartLinkId]);

  const create = useCallback(() => onCreateLink(item), [item, onCreateLink]);

  if (smartLinkId === null || smartLinkStatus === null) {
    return (
      <SMLButton onClick={create} className={styles.smallButton}>
        {intl.formatMessage({ id: 'app.sml.catalog.link.create', defaultMessage: 'Create link' })}
      </SMLButton>
    );
  }

  if (smartLinkStatus === 'draft') {
    return (
      <button type="button" className={styles.linkText} onClick={open}>
        <span className={clsx(styles.pill, styles.pillDraft)}>
          {intl.formatMessage({ id: 'app.sml.catalog.link.draft', defaultMessage: 'Draft page' })}
        </span>
      </button>
    );
  }

  return (
    <button type="button" className={styles.linkText} onClick={open}>
      {smartLinkSlug ?? intl.formatMessage({ id: 'app.sml.catalog.link.open', defaultMessage: 'Open the page' })}
    </button>
  );
};

export const CatalogSmartLinkCell = TypedMemo(CatalogSmartLinkCellComponent);
